import cn from 'classnames';
import React, {
  createRef,
  MutableRefObject,
  PropsWithChildren,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { ElementOrArrayOfElement } from '../common/Types';
import { StringFilter } from './Filters';
import {
  ColumnFilterState,
  ColumnSortState,
  ExposedColumnsMethods,
  ExposedFilterMethods,
  GenericFilterProps,
  SortProps
} from './types';

export interface ColumnsProps {
  children: ElementOrArrayOfElement<ColumnProps>;
  filters?: ColumnFilterState[];
  sortState?: ColumnSortState;
  clearSort?: () => void;
}

export const DatagridColumns = React.forwardRef<
  ExposedColumnsMethods,
  ColumnsProps
>(({ children, clearSort }, ref) => {
  useImperativeHandle(
    ref,
    () => ({
      clearSort: () => {
        if (clearSort) {
          clearSort();
        }
      }
    }),
    [clearSort]
  );

  return <>{children}</>;
});

export type ColumnProps<T = string> = PropsWithChildren<{
  width?: string;
  fixed?: boolean;
  sort?: SortProps;
  filter?: React.ReactElement<FilterProps<T>>;
  isLastColumn?: boolean;
}>;

export const DatagridColumn = React.forwardRef<HTMLDivElement, ColumnProps>(
  (props, ref) => {
    const { children, fixed, filter, sort, width, isLastColumn } = props;
    const classNames = cn('datagrid-column', {
      'datagrid-select datagrid-fixed-column': fixed,
      asc: sort && sort.applied && sort.direction === 'asc',
      desc: sort && sort.applied && sort.direction === 'desc',
      'datagrid-colum--last-column': isLastColumn
    });
    const handleSortBtnClick = () => {
      if (sort && sort.directionChange) {
        sort.directionChange(sort.direction === 'asc' ? 'desc' : 'asc');
      }
    };

    const renderColumnTitle = () =>
      sort && sort.enable ? (
        <button
          type="button"
          className="datagrid-column-title"
          aria-label={sort.label || 'Sort Column'}
          onClick={handleSortBtnClick}>
          {children}
          {sort.direction && (
            <clr-icon
              class="sort-icon"
              shape={cn('arrow', { down: sort.direction === 'desc' })}
            />
          )}
        </button>
      ) : (
        <span className="datagrid-column-title">{children}</span>
      );

    const renderColumnSeparator = () => (
      <div className="datagrid-column-separator" />
    );

    return (
      <div ref={ref} className={classNames} role="columnheader">
        {!fixed ? (
          <div
            className="datagrid-column-flex"
            style={{ width: width ? width : '100px' }}>
            {renderColumnTitle()}
            {filter}
            {renderColumnSeparator()}
          </div>
        ) : (
          <>
            {renderColumnTitle()}
            {renderColumnSeparator()}
          </>
        )}
      </div>
    );
  }
);

export interface FilterProps<T = string> {
  children?: React.ReactElement<GenericFilterProps<T>>;
  applied?: boolean;
  open?: boolean;
  value?: T;
  applyFilter?: (value: T) => void;
  clearFilter?: () => void;
  closePopup?: () => void;
  openPopup?: () => void;
}

export const DatagridFilter = React.forwardRef<
  ExposedFilterMethods | HTMLDivElement,
  FilterProps
>((props, ref) => {
  const {
    children,
    applied,
    open,
    value,
    applyFilter,
    clearFilter,
    closePopup,
    openPopup
  } = props;

  const [key, setKey] = useState(0);
  const popupRef: React.RefObject<HTMLInputElement> = createRef();
  const [focusablePopupElements, setFocusablePopupElements] =
    useState<NodeListOf<Element> | null>();
  const [firstElement, setFirstElement] = useState<HTMLElement | null>();

  useEffect(() => {
    if (!open) return;
    let focusedElBeforeOpen = document.activeElement;
    let PopupEls =
      popupRef.current &&
      popupRef.current.querySelectorAll(
        'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]'
      );
    let firstEl = PopupEls && (PopupEls[0] as HTMLElement);
    firstEl && firstEl.focus();
    setFocusablePopupElements(PopupEls);
    setFirstElement(firstEl);

    return () => {
      if (open && focusedElBeforeOpen) {
        (focusedElBeforeOpen as HTMLElement).focus();
        return;
      }
    };
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    e.keyCode === 27 ? handleEscKey(e) : e.keyCode === 9 ? handleTabKey(e) : '';
  };

  const handleEscKey = (e: React.KeyboardEvent) => {
    e.preventDefault();
    closePopup && closePopup();
  };

  const handleTabKey = (e: React.KeyboardEvent) => {
    const lastElement =
      focusablePopupElements &&
      focusablePopupElements[focusablePopupElements.length - 1];

    if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement && (firstElement as HTMLElement).focus();
    }

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement && (lastElement as HTMLElement).focus();
    }
  };

  const buttonClass = cn('datagrid-filter-toggle', {
    'datagrid-filter-open': open,
    'datagrid-filtered': applied
  });

  const filterShape = cn({
    'filter-grid': !applied,
    'filter-grid-circle': applied
  });

  useImperativeHandle(
    ref,
    () => ({
      clearFilter: () => {
        if (clearFilter) {
          clearFilter();
        }
        setKey(previousKey => previousKey + 1); // kills the filter instance if it is a stateful component
      },
      closeFilter: () => {
        if (closePopup) {
          closePopup();
        }
      }
    }),
    [clearFilter, closePopup]
  );

  const filter = children ? (
    React.cloneElement(children, {
      key: key.toString(),
      apply: applyFilter,
      filterValue: value,
      close: closePopup,
      clear: clearFilter
    })
  ) : (
    <StringFilter
      key={key.toString()}
      apply={applyFilter!}
      filterValue={value}
      close={closePopup!}
      clear={clearFilter!}
    />
  );

  return (
    <div
      ref={ref as MutableRefObject<HTMLDivElement>}
      className="datagrid-filter-toggle-wrapper"
      style={{ position: 'relative' }}>
      <button
        className={buttonClass}
        style={{ position: 'static' }}
        type="button"
        aria-label="Toggle Column filter"
        aria-expanded={!!open}
        onClick={open ? closePopup : openPopup}>
        <clr-icon shape={filterShape} class="is-solid" role="none" />
      </button>
      {open && (
        <div
          className="datagrid-filter"
          ref={popupRef}
          onKeyDown={onKeyDown}
          role="dialog"
          aria-label="filter dialog"
          style={{
            position: 'absolute'
          }}>
          <div className="datagrid-filter-close-wrapper">
            <button className="close" type="button" onClick={closePopup}>
              <clr-icon shape="close" title="close" />
            </button>
          </div>
          {filter}
        </div>
      )}
    </div>
  );
});
